import { Button } from '@/shared/components/ui/Button';

/**
 * Says the server could not be reached, and offers the one thing worth trying.
 *
 * <h2>Why this is shared rather than private to a guard</h2>
 *
 * It was a private function inside `ProtectedRoute`, which was right while one guard needed
 * it. `SignOutFirstRoute` needs it too — it reaches the same dead end for the same reason —
 * and RM-21f says in as many words that no screen carries its own version of this, because a
 * second copy is how two parts of the application come to disagree about whether somebody is
 * signed in. So it moved here on 4 October 2026 rather than being copied.
 *
 * <p>It knows nothing about the session: the retry arrives as a prop. That is what lets it
 * live under `shared/`, which may not import from `features/`.
 *
 * <h2>Deliberately not the sign-in page</h2>
 *
 * Being unable to ask whether somebody is signed in is not evidence that they are not, and
 * sending them to a form they may not need — and whose submit would fail the same way —
 * turns an outage into a lost session. For an emailed link it is worse than that: the address
 * carries a single-use token, and a navigation of any kind spends nothing but loses it.
 */
export function SessionUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <p className="text-lg font-semibold text-[var(--color-ink)]">We cannot reach the server.</p>
      <p className="max-w-md text-sm text-[var(--color-ink-soft)]">
        Your session is still here — nothing has been signed out. This is usually a connection
        problem or a restart that has not finished.
      </p>
      <Button type="button" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
